import { Controller } from "@hotwired/stimulus"

// Calendar view controller — drag-to-reschedule, day overflow popup.
//
// data-calendar-update-url-value="/tasks/:id"
// data-calendar-item-param-value="task"

export default class extends Controller {
  static targets = ["day", "item", "overflow"]
  static values = {
    updateUrl: String,
    itemParam: String
  }

  connect() {
    this.setupDropTargets()
  }

  // ── Setup ────────────────────────────────────────────────

  setupDropTargets() {
    this.dayTargets.forEach(day => {
      day.addEventListener("dragover", this.onDragOver.bind(this))
      day.addEventListener("dragenter", this.onDragEnter.bind(this))
      day.addEventListener("dragleave", this.onDragLeave.bind(this))
      day.addEventListener("drop", this.onDrop.bind(this))
    })
  }

  // ── Drag Events ──────────────────────────────────────────

  onDragStart(event) {
    this.draggedItem = event.currentTarget
    this.draggedItem.classList.add("calendar-item--dragging")
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", this.draggedItem.dataset.itemId)
    requestAnimationFrame(() => {
      this.draggedItem.style.opacity = "0.4"
    })
  }

  onDragEnd() {
    if (this.draggedItem) {
      this.draggedItem.classList.remove("calendar-item--dragging")
      this.draggedItem.style.opacity = ""
    }
    this.dayTargets.forEach(d => d.classList.remove("calendar-day--drop-target"))
    this.draggedItem = null
  }

  onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  onDragEnter(event) {
    event.preventDefault()
    event.currentTarget.classList.add("calendar-day--drop-target")
  }

  onDragLeave(event) {
    const day = event.currentTarget
    const rect = day.getBoundingClientRect()
    if (
      event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom
    ) {
      day.classList.remove("calendar-day--drop-target")
    }
  }

  async onDrop(event) {
    event.preventDefault()
    const day = event.currentTarget
    day.classList.remove("calendar-day--drop-target")

    const itemId = event.dataTransfer.getData("text/plain")
    const newDate = day.dataset.date
    const item = this.element.querySelector(`[data-item-id='${itemId}']`)

    if (!item || !newDate) return

    // Move item to this day's list
    const itemsContainer = day.querySelector(".calendar-day-items")
    if (itemsContainer) {
      item.style.opacity = ""
      item.classList.remove("calendar-item--dragging")
      itemsContainer.appendChild(item)
    }

    // Persist the change
    await this.persistDateChange(itemId, newDate)
  }

  // ── Day Overflow ─────────────────────────────────────────

  showOverflow(event) {
    const dayEl = event.currentTarget.closest(".calendar-day")
    const date = dayEl.dataset.date
    const items = dayEl.querySelectorAll(".calendar-item")

    // Build popup
    let popup = document.getElementById("calendar-overflow-popup")
    if (!popup) {
      popup = document.createElement("div")
      popup.id = "calendar-overflow-popup"
      popup.className = "calendar-overflow-popup"
      document.body.appendChild(popup)
    }

    const formattedDate = new Date(date + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric"
    })

    popup.innerHTML = `
      <div class="calendar-overflow-header">
        <span>${formattedDate}</span>
        <button data-action="calendar#closeOverflow" class="calendar-overflow-close">&times;</button>
      </div>
      <div class="calendar-overflow-list"></div>
    `

    const list = popup.querySelector(".calendar-overflow-list")
    items.forEach(item => {
      const clone = item.cloneNode(true)
      clone.classList.remove("hidden")
      list.appendChild(clone)
    })

    // Position near the day cell
    const rect = dayEl.getBoundingClientRect()
    popup.style.top = `${rect.bottom + window.scrollY + 4}px`
    popup.style.left = `${rect.left + window.scrollX}px`
    popup.classList.add("calendar-overflow-popup--visible")

    // Close on outside click
    const close = (e) => {
      if (!popup.contains(e.target) && !dayEl.contains(e.target)) {
        popup.classList.remove("calendar-overflow-popup--visible")
        document.removeEventListener("mousedown", close)
      }
    }
    setTimeout(() => document.addEventListener("mousedown", close), 0)
  }

  closeOverflow() {
    const popup = document.getElementById("calendar-overflow-popup")
    popup?.classList.remove("calendar-overflow-popup--visible")
  }

  // ── Persist ──────────────────────────────────────────────

  async persistDateChange(itemId, newDate) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    const url = this.updateUrlValue.replace(":id", itemId)
    const body = {}
    body[this.itemParamValue] = { due_date: newDate }

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)
    } catch (error) {
      console.error("Failed to reschedule:", error)
    }
  }
}
